
import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Image, Upload, Loader2, FileImage, FilePlus, ChevronDown, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { toast } from "sonner";

const MediaLibrary = () => {
  useDocumentTitle("Media Library");
  const [isUploading, setIsUploading] = useState(false);
  const [view, setView] = useState<"grid" | "list">("grid");
  const [searchQuery, setSearchQuery] = useState("");
  const [fileType, setFileType] = useState<"all" | "images" | "documents">("all");

  const handleUpload = () => {
    toast.info("Media uploads will be available in Phase 3");
    setIsUploading(true);
    setTimeout(() => {
      setIsUploading(false);
      toast.success("Feature coming soon!");
    }, 1500);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="space-y-6"
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Media Library</h1>
          <p className="text-muted-foreground">Manage your images and media files</p>
        </div>
        <Button onClick={handleUpload} disabled={isUploading} className="flex items-center gap-2">
          {isUploading ? (
            <Loader2 size={16} className="animate-spin" />
          ) : (
            <Upload size={16} />
          )}
          <span>Upload Media</span>
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search media files..."
            className="pl-8"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <div className="w-full sm:w-auto">
            <Select
              value={fileType}
              onValueChange={(value: "all" | "images" | "documents") => setFileType(value)}
            >
              <SelectTrigger className="w-full sm:w-40">
                <SelectValue placeholder="File type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Files</SelectItem>
                <SelectItem value="images">Images</SelectItem>
                <SelectItem value="documents">Documents</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex rounded-md border">
            <Button
              variant={view === "grid" ? "default" : "ghost"}
              size="icon"
              className="rounded-none rounded-l-md"
              onClick={() => setView("grid")}
            >
              <Image className="h-4 w-4" />
            </Button>
            <Button
              variant={view === "list" ? "default" : "ghost"}
              size="icon"
              className="rounded-none rounded-r-md"
              onClick={() => setView("list")}
            >
              <FileImage className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      <Card className="border-dashed border-2 p-10">
        <CardContent className="flex flex-col items-center justify-center p-0 text-center">
          <FilePlus className="h-10 w-10 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">Media Library</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Available in Phase 3. Here you'll be able to upload and manage images for your blog posts and newsletters.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 w-full max-w-lg mt-6">
            <div className="border rounded p-3 flex flex-col items-center justify-center">
              <Image className="h-6 w-6 text-muted-foreground mb-2" />
              <p className="text-xs font-medium">Image Uploads</p>
              <Badge className="mt-1" variant="outline">Coming soon</Badge>
            </div>
            <div className="border rounded p-3 flex flex-col items-center justify-center">
              <FileImage className="h-6 w-6 text-muted-foreground mb-2" />
              <p className="text-xs font-medium">Galleries</p>
              <Badge className="mt-1" variant="outline">Coming soon</Badge>
            </div>
            <div className="border rounded p-3 flex flex-col items-center justify-center">
              <FilePlus className="h-6 w-6 text-muted-foreground mb-2" />
              <p className="text-xs font-medium">File Management</p>
              <Badge className="mt-1" variant="outline">Coming soon</Badge>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default MediaLibrary;
